import { ColDef, ColGroupDef } from "ag-grid-community";

import { IntegrationType } from "constants/integrations";

import { IntegrationBusinessRenderer } from "lib/agGrid/columns/shortCode";
import getters from "lib/agGrid/getters";
import { ColumnType } from "components/AgGrid/constants";

export const XeroIntegrationBusinessColId = {
  XERO_INTEGRATION_BUSINESS: "6487f4bf-cdd6-4c9f-a0c3-ea90de3d4e18",
  BALANCES: "6e880cbd-f87f-46df-b38a-358e28fa6f85",
  BALANCES_ACCOUNTS_RECEIVABLE_OUTSTANDING:
    "a256e3ac-dbc6-493e-b1f3-9f07584c3a66",
  BALANCES_ACCOUNTS_RECEIVABLE_OVERDUE: "621a31c3-aab1-4ba2-9ab5-c98463a7bfd2",
  BALANCES_ACCOUNTS_PAYABLE_OUTSTANDING: "53f1e9af-79d6-4f00-aa93-d1cca15383cb",
  BALANCES_ACCOUNTS_PAYABLE_OVERDUE: "b6b08842-451a-4cee-8901-499029807ddd",
  OUTSTANDING_TOTAL: "dff2ca7e-ba08-4d35-ace3-dc291fa28381",
  OVERDUE_TOTAL: "3bc003d1-bec3-4bb3-97a7-e81ba9ef7768",
} as const;

interface XeroIntegrationBusinessRow {
  xeroIntegrationBusiness: IntegrationBusiness;
}

const XeroBalanceColumns: Record<string, ColDef<XeroIntegrationBusinessRow>> = {
  [XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_RECEIVABLE_OUTSTANDING]: {
    colId:
      XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_RECEIVABLE_OUTSTANDING,
    field: "xeroIntegrationBusiness.balances.AccountsReceivable.Outstanding",
    headerName: "Receivable Outstanding ($)",
    width: 120,
    type: ColumnType.CURRENCY,
  },

  [XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_RECEIVABLE_OVERDUE]: {
    colId: XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_RECEIVABLE_OVERDUE,
    field: "xeroIntegrationBusiness.balances.AccountsReceivable.Overdue",
    headerName: "Receivable Overdue ($)",
    width: 120,
    type: ColumnType.CURRENCY,
  },

  [XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_PAYABLE_OUTSTANDING]: {
    colId: XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_PAYABLE_OUTSTANDING,
    field: "xeroIntegrationBusiness.balances.AccountsPayable.Outstanding",
    headerName: "Payable Outstanding ($)",
    width: 120,
    type: ColumnType.CURRENCY,
  },

  [XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_PAYABLE_OVERDUE]: {
    colId: XeroIntegrationBusinessColId.BALANCES_ACCOUNTS_PAYABLE_OVERDUE,
    field: "xeroIntegrationBusiness.balances.AccountsPayable.Overdue",
    headerName: "Payable Overdue ($)",
    width: 120,
    type: ColumnType.CURRENCY,
  },

  [XeroIntegrationBusinessColId.OUTSTANDING_TOTAL]: {
    colId: XeroIntegrationBusinessColId.OUTSTANDING_TOTAL,
    field: "xeroIntegrationBusiness.balances",
    headerName: "Balance ($)",
    width: 120,
    valueGetter: getters.xeroBalanceGetter("Outstanding"),
    type: ColumnType.CURRENCY,
  },
  [XeroIntegrationBusinessColId.OVERDUE_TOTAL]: {
    colId: XeroIntegrationBusinessColId.OVERDUE_TOTAL,
    field: "xeroIntegrationBusiness.balances",
    headerName: "Overdue Balance ($)",
    width: 120,
    valueGetter: getters.xeroBalanceGetter("Overdue"),
    type: ColumnType.CURRENCY,
  },
};

export const XeroIntegrationBusinessDef: Record<
  string,
  ColDef<XeroIntegrationBusinessRow> | ColGroupDef<XeroIntegrationBusinessRow>
> = {
  [XeroIntegrationBusinessColId.XERO_INTEGRATION_BUSINESS]: {
    colId: XeroIntegrationBusinessColId.XERO_INTEGRATION_BUSINESS,
    field: "xeroIntegrationBusiness",
    headerName: "Xero Business",
    width: 120,
    cellRenderer: IntegrationBusinessRenderer,
    valueGetter: getters.xeroBusinessGetter,
    filterValueGetter: getters.isDefinedGetter,
    cellRendererParams: {
      businessKeys: ["business"],
      complainIfMissing: true,
      useLogoAndTooltip: true,
      integrationType: IntegrationType.Xero,
    },
  },
  [XeroIntegrationBusinessColId.BALANCES]: {
    headerName: "Xero Account Balances",
    children: Object.values(XeroBalanceColumns),
  },
};
